import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  SignIn,
  useClerk,
} from "@clerk/clerk-react";
import { useCallback } from "react";

import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

import { AnalyticsProvider } from "../Analytics/AnalyticsProvider";
import { UserIdentifier } from "../Analytics/UserIdentifier";
import { CallManager } from "../Call/CallManager";
import { NetworkStatusProvider } from "../NetworkStatus/NetworkStatusProvider";
import { PlatformProvider } from "../Platform/PlatformProvider";
import { ClerkAuthObserver } from "./ClerkAuthObserver";
import { useAuth } from "./UnifiedAuthProvider";

const POSTHOG_ENABLED = import.meta.env.VITE_POSTHOG_ENABLED;
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

function WebAppContent() {
  const { isAuthenticated, clearCoreAuthState } = useAuth();
  const { signOut } = useClerk();

  const handleWebAppLogout = useCallback(async () => {
    await signOut();
    clearCoreAuthState();
    // For the web, Clerk's <SignedOut> component will handle the UI redirect.
  }, [signOut, clearCoreAuthState]);

  return (
    <PlatformProvider logout={handleWebAppLogout}>
      <AnalyticsProvider
        posthogKey={POSTHOG_KEY}
        posthogEnabled={POSTHOG_ENABLED}
      >
        <NetworkStatusProvider>
          <TooltipProvider>
            <main className="h-screen" data-testid="app-loaded">
              <SignedIn>
                <ClerkAuthObserver />
                {isAuthenticated ? (
                  <>
                    <UserIdentifier />
                    <CallManager />
                  </>
                ) : (
                  <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                    <p>Synchronizing authentication...</p>
                    {/* This button is now redundant, as AppSettingsUI will use the context */}
                  </div>
                )}
              </SignedIn>
              <SignedOut>
                <div className="flex min-h-screen items-center justify-center">
                  <SignIn />
                </div>
              </SignedOut>
            </main>
          </TooltipProvider>
          <Toaster />
        </NetworkStatusProvider>
      </AnalyticsProvider>
    </PlatformProvider>
  );
}

export function WebApp() {
  if (!CLERK_PUBLISHABLE_KEY) {
    throw new Error("VITE_CLERK_PUBLISHABLE_KEY is not set");
  }

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <WebAppContent />
    </ClerkProvider>
  );
}
